import { action, set } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type StateService from 'garaje/services/state';
import { DATE_FIELD_FORMAT_OPTIONS, DateFieldFormat } from 'garaje/utils/enums';
import moment from 'moment-timezone';
import { localCopy } from 'tracked-toolbox';

type MomentZoneWithCountries = moment.MomentZone & { countries?: () => string[] };

function getCountryForTimezone(timezone: string): string | undefined {
  const zone = moment.tz.zone(timezone) as MomentZoneWithCountries | null;
  const countries = zone?.countries?.();
  if (countries && countries.length > 0) return countries[0];
  const key = timezone.toLowerCase().replace(/\//g, '_');
  const linked = (moment.tz as unknown as { _links: Record<string, string> })._links[key];
  return linked ? (moment.tz.zone(linked) as MomentZoneWithCountries | null)?.countries?.()[0] : undefined;
}

function detectDateFormatFromTimezone(timezone: string): DateFieldFormat {
  try {
    const region = getCountryForTimezone(timezone);
    if (!region) return DateFieldFormat.MM_DD_YYYY;

    const locale = new Intl.Locale(`und-${region}`).maximize().toString();
    const formatter = new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
    const parts = formatter.formatToParts(new Date(2000, 11, 31));
    const order = parts.filter((p) => ['year', 'month', 'day'].includes(p.type)).map((p) => p.type);
    const separator = parts.find((p) => p.type === 'literal')?.value || '/';

    if (order[0] === 'year') return DateFieldFormat.YYYY_MM_DD;
    if (order[0] === 'day' && separator === '.') return DateFieldFormat.DD_DOT_MM_YYYY;
    if (order[0] === 'day') return DateFieldFormat.DD_MM_YYYY;
    return DateFieldFormat.MM_DD_YYYY;
  } catch {
    return DateFieldFormat.MM_DD_YYYY;
  }
}

interface SignInFieldsDateFieldComponentArgs {
  canDelete: boolean;
  deleteFieldAction: (_: unknown) => unknown;
  field: {
    name: string;
    required: boolean;
    dateFormat: DateFieldFormat | null | undefined;
    isInvalid?: boolean;
    sensitive?: boolean;
    _content?: unknown;
  };
  handleItem: unknown;
  isConditional?: boolean;
  isDisabled?: boolean;
  isRTL: boolean;
  showSensitiveShields?: boolean;
}

export default class SignInFieldsDateFieldComponent extends Component<SignInFieldsDateFieldComponentArgs> {
  @service declare state: StateService;

  @localCopy('args.canDelete', true) declare canDelete: boolean;
  @localCopy('args.isDisabled', false) declare isDisabled: boolean;

  inputId = 'input-' + guidFor(this);
  dateFormatOptions = DATE_FIELD_FORMAT_OPTIONS;

  get isDraggable(): boolean {
    return !this.args.isConditional && !this.args.isDisabled;
  }

  get defaultDateFormat(): DateFieldFormat {
    try {
      const timezone = this.state.currentLocation?.timezone;
      if (timezone) {
        return detectDateFormatFromTimezone(timezone);
      }
      return DateFieldFormat.MM_DD_YYYY;
    } catch {
      return DateFieldFormat.MM_DD_YYYY;
    }
  }

  get selectedDateFormat(): { value: DateFieldFormat; label: string } {
    const currentFormat = this.args.field.dateFormat || this.defaultDateFormat;
    return this.dateFormatOptions.find((option) => option.value === currentFormat) || this.dateFormatOptions[0]!;
  }

  @action
  initializeDateFormat(): void {
    schedule('afterRender', () => {
      if (!this.args.field.dateFormat) {
        set(this.args.field, 'dateFormat', this.defaultDateFormat);
      }
    });
  }

  @action
  updateDateFormat(selected: { value: DateFieldFormat; label: string }): void {
    set(this.args.field, 'dateFormat', selected.value);
  }
}
